import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http'
import { APP_INITIALIZER, EnvironmentProviders, ErrorHandler, NgModule, Provider } from '@angular/core'
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt'
import { RepositoryState } from '@ftr/api-recording-vault'
import { API_CONFIGURATION, AUTH_CONFIGURATION, AUTH_TOKEN_FETCHER } from '@ftr/api-shared'
import { DialogComponent, SimpleWindowRefService, SizesService, TrackingService } from '@ftr/foundation'
import { CourtSystemsState } from '@ftr/ui-court-system'
import { FeatureFlagState, LAUNCH_DARKLY_CONFIG, LaunchDarklyModule } from '@ftr/ui-feature-flags'
import { SearchManyState, SearchState } from '@ftr/ui-search'
import { AuthTokenFetcherService, AuthenticationService, UserState } from '@ftr/ui-user'
import { VocabularyState } from '@ftr/ui-vocab'
import '@js-joda/timezone-10-year-range'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { NgxsModule } from '@ngxs/store'
import { InlineSVGModule } from 'ng-inline-svg'
import { DesktopModule } from '~app/features/desktop/desktop.module'
import { HeaderUnderlayModule } from '~app/features/header-underlay/header-underlay.module'
import { JobServiceModule } from '~app/services/job-service/job-service.module'
import { LaunchDarklyAttributeService } from '~app/services/launch-darkly-attribute/launch-darkly-attribute.service'
import { WindowRefService } from '~app/services/window/window-ref.service'
import { CourtroomState } from '~store/courtroom/courtroom.state'
import { NGXS_OPTIONS } from '~store/ngxs-options'
import { OrderAccountState } from '~store/order-account/order-account.state'
import { OrderState } from '~store/order/order.state'
import { RealTimeSearchState } from '~store/real-time-search/real-time-search.state'
import { RealTimeSttState } from '~store/real-time-stt/real-time-stt.state'
/*
 * Platform and Environment providers/directives/pipes
 */
import {
  BreadcrumbModule,
  ConfirmationModalComponent,
  PageComponent,
  PortalComponent,
  ToastModule,
} from '@ftr/foundation'
import { ErrorHandlerService, OBSERVABILITY_CONFIGURATION } from '@ftr/ui-observability'
import { PlaybackState } from '@ftr/ui-playback'
import { PostLoginNotificationComponent } from '~app/features/audit-log/post-login-notification/post-login-notification.component'
import { MfaSetupModalComponent } from '~app/features/multifactor-authentication/setup-modal/mfa-setup-modal.component'
import { SimpleTrackingService } from '~app/services/analytics/simple-tracking.service'
import { RecordKeyPressToStoreService } from '~app/services/record-key-press-to-store/record-key-press-to-store.service'
import { environment } from '~environments/environment'
import { AnnotationsState } from '~store/annotations/annotations.state'
import { CasesState } from '~store/cases/cases.state'
import { KeyPressState } from '~store/key-press/key-press.state'
import { MfaRequirementsState } from '~store/mfa-requirements/mfa-requirements.state'
import { RecordingsFilterState } from '~store/recordings-filter/recordings-filter.state'
import { SharedRecordingState } from '~store/shared-recording/shared-recording.state'
import { UseConditionsState } from '~store/use-conditions/use-conditions.state'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { CoreModule } from './core/core.module'
import { FooterModule } from './core/footer/footer.module'
import { HeaderModule } from './core/header/header.module'
import { OrderSharedModule } from './order-shared/order-shared.module'
import { ConfigurationService } from './services/configuration/configuration.service'
import { HeaderSizeService } from './services/header-size/header-size.service'
import { AuthRedirectInterceptor } from './services/http-client/auth-redirect.interceptor'
import { jwtOptionsFactory } from './services/http-client/jwt-options.factory'
import { LoggingInterceptor } from './services/http-client/logging.interceptor'

// Application wide providers
const APP_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  { provide: ErrorHandler, useClass: ErrorHandlerService },
  { provide: TrackingService, useClass: SimpleTrackingService },
  { provide: SimpleWindowRefService, useClass: WindowRefService },
  { provide: SizesService, useClass: HeaderSizeService },
  { provide: LAUNCH_DARKLY_CONFIG, useClass: ConfigurationService },
  { provide: API_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_CONFIGURATION, useClass: ConfigurationService },
  { provide: OBSERVABILITY_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_TOKEN_FETCHER, useClass: AuthTokenFetcherService },
  // Wire up launch darkly to apply attributes globally from the store
  {
    provide: APP_INITIALIZER,
    useFactory: (service: LaunchDarklyAttributeService) => () => service.observeAndApplyAttributes(),
    deps: [LaunchDarklyAttributeService, LAUNCH_DARKLY_CONFIG],
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    useFactory: (service: RecordKeyPressToStoreService) => () => service.syncKeyPressesToStore(),
    deps: [RecordKeyPressToStoreService],
    multi: true,
  },
]

const httpInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthRedirectInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
]

/**
 * `AppModule` is the main entry point into Angular's bootstrap process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    CommonModule,
    NgxsModule.forRoot(
      [
        UserState,
        SearchState,
        SearchManyState,
        PlaybackState,
        CourtSystemsState,
        VocabularyState,
        RepositoryState,
        OrderState,
        OrderAccountState,
        CourtroomState,
        FeatureFlagState,
        RealTimeSttState,
        RealTimeSearchState,
        UseConditionsState,
        RecordingsFilterState,
        MfaRequirementsState,
        SharedRecordingState,
        AnnotationsState,
        KeyPressState,
        CasesState,
      ],
      NGXS_OPTIONS,
    ),
    FooterModule,
    HeaderModule,
    HttpClientModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AuthenticationService, ConfigurationService],
      },
    }),
    InlineSVGModule.forRoot(),
    CoreModule.forRoot(),
    OrderSharedModule.forRoot(),
    JobServiceModule.forRoot(),
    AppRoutingModule,
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
      name: 'FTR-WEB',

      // These are super noisy and polling, essentially making the devtools unusable
      actionsBlacklist: [
        '\\[RealTimeStt\\] Set Real Time Stt Transcript Dividers',
        '\\[Playback\\] Reevaluate on record state',
      ],
    }),
    BreadcrumbModule,
    ToastModule,
    DesktopModule,
    HeaderUnderlayModule,
    MfaSetupModalComponent,
    PostLoginNotificationComponent,
    PortalComponent,
    PageComponent,
    LaunchDarklyModule.forRoot(),
    DialogComponent,
    ConfirmationModalComponent,
  ],
  providers: [APP_PROVIDERS, httpInterceptorProviders],
})
export class AppModule {}
